import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Shield, AlertTriangle } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dc2ea8e9-88c1-4191-a09e-3c70ac39f874/artifacts/lxlb74jo_IMG_9876.jpeg";
const ACCENT = "#4d7cff";

const AgeVerification = () => {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Skip age gate on reset-password page
    if (location.pathname === "/reset-password") return;
    const ageVerified = localStorage.getItem("jackroyals_age_verified");
    if (!ageVerified) {
      setIsVisible(true);
      document.body.style.overflow = "hidden";
    }
    return () => { document.body.style.overflow = "unset"; };
  }, [location.pathname]);

  const handleConfirm = () => {
    localStorage.setItem("jackroyals_age_verified", "true");
    localStorage.setItem("jackroyals_age_verified_date", new Date().toISOString());
    setIsVisible(false);
    document.body.style.overflow = "unset";
  };

  const handleDecline = () => {
    window.location.href = "https://www.google.com";
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-sm" style={{ background: 'rgba(2,3,10,0.97)' }}>
      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(77,124,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(77,124,255,0.3) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

      <div className="relative max-w-md w-full">
        {/* Glow effect */}
        <div className="absolute -inset-1 rounded-2xl blur-lg opacity-25 animate-pulse" style={{ background: `linear-gradient(135deg, ${ACCENT}, #7f5dff, ${ACCENT})` }} />
        
        <div className="relative rounded-2xl border p-8 shadow-2xl" style={{ background: 'linear-gradient(to bottom, #111a33, #070b19)', borderColor: `${ACCENT}25` }}>
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="JackRoyals" className="w-14 h-14 rounded-full object-cover" style={{ boxShadow: `0 0 20px ${ACCENT}40` }} />
              <span className="text-3xl font-black">
                <span className="text-white">Jack</span><span style={{ color: ACCENT }}>Royals</span>
              </span>
            </div>
          </div>

          {/* 18+ Badge */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full flex items-center justify-center border-2" style={{ background: `${ACCENT}10`, borderColor: `${ACCENT}40` }}>
              <span className="text-4xl font-black" style={{ color: ACCENT }}>18+</span>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-center text-white mb-2">
            Age Verification Required
          </h2>
          <p className="text-center mb-6" style={{ color: '#8fa2d6' }}>
            You must be 18 years or older to access this website.
          </p>

          {/* Notice Box */}
          <div className="rounded-xl p-4 mb-6 border" style={{ background: `${ACCENT}08`, borderColor: `${ACCENT}20` }}>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: ACCENT }} />
              <div className="text-sm">
                <p className="font-medium mb-1" style={{ color: '#c7d3ff' }}>Important Notice</p>
                <p style={{ color: '#8fa2d6' }}>
                  JackRoyals is a sweepstakes entertainment platform. No purchase necessary. By entering, you confirm you meet the legal age requirement in your jurisdiction.
                </p>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleConfirm}
              className="w-full h-14 text-lg font-bold text-white rounded-xl shadow-lg"
              style={{ background: ACCENT, boxShadow: `0 0 20px ${ACCENT}40` }}
              data-testid="age-verify-confirm"
            >
              <Shield className="w-5 h-5 mr-2" />
              Yes, I am 18 or older
            </Button>
            
            <Button
              onClick={handleDecline}
              variant="outline"
              className="w-full h-12 font-medium rounded-xl"
              style={{ borderColor: '#1f2a4d', color: '#8fa2d6' }}
              data-testid="age-verify-decline"
            >
              No, I am under 18
            </Button>
          </div>

          {/* Legal Links */}
          <div className="mt-6 pt-6" style={{ borderTop: '1px solid #1f2a4d' }}>
            <p className="text-xs text-center" style={{ color: '#5a6b8a' }}>
              By clicking "Yes, I am 18 or older" you agree to our{" "}
              <Link to="/terms" className="hover:underline" style={{ color: ACCENT }}>Terms & Conditions</Link>{" "}
              and{" "}
              <Link to="/privacy" className="hover:underline" style={{ color: ACCENT }}>Privacy Policy</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgeVerification;
